import {
  animate,
  MotionValue,
  useMotionTemplate,
  useMotionValue,
  useMotionValueEvent,
} from 'motion/react';

const TRANSPARENT = `#0000`;
const BLACK = `#000`;

const DEFAULT_MASK_WIDTH = 120;
const DEFAULT_EDGE_THRESHOLD = 0.03;

export type UseScrollOverflowMaskOptions = {
  /**
   * Width of the fade zones at left and right edges.
   * @default 120
   */
  maskWidth?: number;
  /**
   * Additional CSS mask layers appended to the horizontal gradient (e.g. Tabs primary bottom border).
   */
  extraMaskLayers?: string;
  /**
   * Scroll progress threshold for treating the container as at the start/end edge.
   * End edge is `1 - edgeThreshold`.
   * @default 0.03
   */
  edgeThreshold?: number;
};

/**
 * Animated horizontal scroll fade mask for use with `mask-image` on a Motion element.
 * Pair with `useScrollProgress` and `useIsHorizontalOverflow` (see Tabs / Suggestions).
 *
 * @remarks `maskWidth` and `extraMaskLayers` are captured at mount via `useMotionTemplate`
 * and do not update if options change after the initial render.
 */
export const useScrollOverflowMask = (
  scrollXProgress: MotionValue<number>,
  options: UseScrollOverflowMaskOptions = {},
) => {
  const {
    maskWidth = DEFAULT_MASK_WIDTH,
    extraMaskLayers = '',
    edgeThreshold = DEFAULT_EDGE_THRESHOLD,
  } = options;

  const leftColor = useMotionValue(BLACK);
  const rightColor = useMotionValue(TRANSPARENT);
  const endThreshold = 1 - edgeThreshold;

  const maskImage = useMotionTemplate`linear-gradient(90deg, ${leftColor}, ${BLACK} ${maskWidth}px, ${BLACK} calc(100% - ${maskWidth}px), ${rightColor})${extraMaskLayers}`;

  useMotionValueEvent(scrollXProgress, 'change', (value: number) => {
    if (value <= edgeThreshold) {
      animate(leftColor, BLACK);
      animate(rightColor, TRANSPARENT);
    } else if (value >= endThreshold) {
      animate(leftColor, TRANSPARENT);
      animate(rightColor, BLACK);
    } else {
      animate(leftColor, TRANSPARENT);
      animate(rightColor, TRANSPARENT);
    }
  });

  return maskImage;
};
